<template>
	<b-card id="clickRateByCampaign">
		<b-card-title ref="left_header" class="mb-4">
			<div class="title" :title="title">
				{{ title }}
				<font-awesome-icon id="clickRateByCampaignTooltip" :icon="['fas', 'info-circle']" class="mr-1"/>
				<b-tooltip class="mt-2 mr-2" triggers="hover" right target="clickRateByCampaignTooltip">
					<span v-html="$t('Analytics.Tooltips.' + tooltipText)"></span>
				</b-tooltip>
			</div>
			<b-col class="filter-container">
				<span v-html="breadCrumb" @click="removeFilter"></span>
			</b-col>

			<b-dropdown v-if="!inProgress && showAvgDiffChartButton" right class="tab-dropdown">
				<template v-slot:button-content>
					<label class="sr-only">{{ $t('Analytics.AverageDifficulty') }}</label>
					<font-awesome-icon :icon="['fas', 'ellipsis-v']" />
				</template>
				<b-dropdown-item @click="toggleAverageDifficultyChart(CAMPAIGN)">{{ $t('Analytics.ClickRateByCampaign') }}
				</b-dropdown-item>
				<b-dropdown-item @click="toggleAverageDifficultyChart(CAMP_AVG)">{{ $t('Analytics.ClickRateWithDifficulty') }}
				</b-dropdown-item>
				<b-dropdown-item @click="toggleAverageDifficultyChart(NORMALIZED)">{{ $t('Analytics.ClickRateByCampaignNormalized') }}
				</b-dropdown-item>
			</b-dropdown>

			<b-spinner class="spinner" variant="primary" :label="$t('General.Loading')" v-if="inProgress"></b-spinner>
			<font-awesome-icon v-if="!inProgress && !ads.expanded" class="expand-icon" :icon="['fas', 'expand']" @click="ads.expanded = !ads.expanded" />
			<font-awesome-icon v-if="!inProgress && ads.expanded" class="compress-icon" :icon="['fas', 'compress']" @click="ads.expanded = !ads.expanded" />
		</b-card-title>
		<cg-bar-chart v-if="showChart" :chart-data="chartData" :options="options" ref="chart"></cg-bar-chart>
	</b-card>
</template>

<script>
import BarChart from "@/components/charts/wrappers/BarChart";
import CompanyService from "@/services/company.service";
import colors from "@/components/charts/utils/colors";
import { truncateStr } from "@/components/utils";
import * as pattern from 'patternomaly';

const CAMPAIGN = 1;
const CAMP_AVG = 2;
const NORMALIZED = 3;

export default {
	components: {
		"cg-bar-chart": BarChart
	},
	data() {
		return {
			myself: "campaign",
			selectedIndex: null,
			breadCrumb: "",
			chartData: {},
			options: {},
			inProgress: false,
			showChart: false,
			currentChart: 0,
			myselfClicked: false,
			showAvgDiffChartButton: true
		};
	},
	props: {
		ads: Object
	},
	computed: {
		title() {
			return (this.currentChart == this.CAMPAIGN || (this.breadCrumb && this.ads.hasOtherFilters(this.myself) && this.currentChart == this.CAMP_AVG)) ? this.$t('Analytics.ClickRateByCampaign') : 
				(this.currentChart == this.CAMP_AVG ? this.$t('Analytics.ClickRateWithDifficulty')  : this.$t('Analytics.ClickRateByCampaignNormalized'));
		},
		tooltipText() {
			return (this.currentChart == this.CAMPAIGN || (this.breadCrumb && this.ads.hasOtherFilters(this.myself) && this.currentChart == this.CAMP_AVG)) ? 'ClickRateByCampaignInfo' : 
				(this.currentChart == this.CAMP_AVG ? 'ClickRateByCampaignWithDifficultyInfo'  : 'NormalizedClickRateByCampaignInfo');
		}
	},
	created() {
        this.CAMPAIGN = CAMPAIGN;
        this.CAMP_AVG = CAMP_AVG;
		this.NORMALIZED = NORMALIZED;

		this.currentChart = this.CAMPAIGN;
    },
	async mounted() {
		await this.init();

		this.ads.bus.$on("filter_changed", () => {
			this.breadCrumb = this.ads.getInteractiveBreadcrumb();
			this.refresh();
		});
	},
	methods: {
		async init() {
			this.setOptions();

			let data = await this.getData();
			if (data) this.createChart(data);
		},
		async refresh() {
			const hasFilters = this.ads.hasOtherValidFilters(this.myself);
			let data = !this.myselfClicked ? await this.getData() : null;

			if (hasFilters) {
				this.showAvgDiffChartButton = false;
				if (data) this.addDataset(data);
			} else {
				this.showAvgDiffChartButton = true;
				this.removeDataset();
			}
			this.toggleActive();
		},
		// Retrieve the clicker rate by campaign
		async getData() {
			this.inProgress = true;

			const filters = this.ads.getFiltersFor(this.myself);
			const queryParams = filters ? filters : {};
			queryParams.ctx = this.ads.ctx;

			let result = this.ads.companyId ? await CompanyService.getAnalyticsSummary(this.ads.companyId, this.myself, queryParams) : {};
			this.inProgress = false;
			this.showChart = true;
			return result.data;
		},
		createChart(data) {
			this.chartData = {
				labels: [],
				campaignId: [],
				datasets: []
			};
			this.addDataset(data);
			this.addClickHandler();
			this.createLegend();
		},
		drawAverageDifficulty(dataset) {
			return {
				pointBorderColor: [],
				pointBackgroundColor: colors.plain.green,
				pointRadius: [],
				yAxisID: 'y-axis-avg',
				label: this.$t('Analytics.AverageDifficulty'),
				data: dataset,
				borderColor: colors.alpha.green,
				backgroundColor: 'transparent',
				type: 'line',
				lineTension: 0,
				bezierCurve: false,

				options: {
					elements: {
						point: {
							pointHoverBorderWidth: 2
						},
						line: {
							tension: 0,
							borderWidth: 2
						}
					},
					responsive: true,
					maintainAspectRatio: false,
					animation: {
						animateRotate: true,
						animateScale: true
					},
					plugins: {
						datalabels: {
							display: false
						}
					}
				}
			}
		},
		drawAverageDifficultyYAxis(show) {
			let chart = this.$refs.chart.$data._chart;

			chart.options.scales.yAxes[1].display = show;

			chart.update();
		},
		toggleAverageDifficultyChart(chart) {
			if(this.currentChart == chart) {
				return;
			}

			this.currentChart = chart;
			this.chartData.datasets[0].data = this.currentChart == this.NORMALIZED? this.chartData.datasets[0].normalized : this.chartData.datasets[0].clickRate;
			this.chartData.datasets[0].backgroundColor =  
				this.chartData.datasets[0].backgroundColor.map(() => this.currentChart == this.NORMALIZED? pattern.draw('diagonal', colors.alpha.blue) : colors.alpha.blue);
			this.removeDataset();
		},
		addDataset(data) {
			const first = this.chartData.datasets.length == 0;

			let minAvgDiff = data.reduce((min, p) => p.avg_difficulty < min ? p.avg_difficulty : min, data[0].avg_difficulty);

			if (first) {
				let dataset = {
					label: [this.$t('Analytics.Campaign')],
					data: [],
					sent: [],
					clicked: [],
					period: [],
					active: [],
					avgDifficulty: [],
					clickRate: [],
					normalized: [],
					backgroundColor: [],
					borderColor: [],
					borderWidth: 1,
					maxBarThickness: 150
				};

				data.reverse().forEach((summary) => {
					let clickRate = (100 * (summary.clicked / summary.sent)).toFixed(1);
					let bgColor = this.currentChart == this.NORMALIZED? pattern.draw('diagonal', colors.alpha.blue) : colors.alpha.blue;
					dataset.backgroundColor.push(bgColor);
					dataset.borderColor.push(colors.plain.blue);

					this.chartData.campaignId.push(summary.campaign_id);
					this.chartData.labels.push(summary.name);
					dataset.yAxisID = 'y';
					dataset.active.push(false);
					dataset.data.push(clickRate);
					dataset.clickRate.push(clickRate);
					dataset.sent.push(summary.sent);
					dataset.clicked.push(summary.clicked);
					dataset.avgDifficulty.push(summary.avg_difficulty);
					dataset.normalized.push(((clickRate/summary.avg_difficulty)*minAvgDiff).toFixed(1));
					let planned_start = this.$moment.utc(summary.planned_start).local().locale(this.$i18n.locale).format("YYYY-MM-DD");
					let planned_end = this.$moment.utc(summary.planned_end).local().locale(this.$i18n.locale).format("YYYY-MM-DD");
					dataset.period.push(planned_start + " - " + planned_end);
				});

				this.chartData.datasets[0] = dataset;
			} else {
				let dataset = {
					label: this.ads.getBreadcrumbFor(this.myself),
					data: this.chartData.datasets[0].data.map(() => null),
					sent: this.chartData.datasets[0].sent.map(() => null),
					clicked: this.chartData.datasets[0].clicked.map(() => null),
					period: this.chartData.datasets[0].period.map(() => null),
					backgroundColor: this.chartData.datasets[0].backgroundColor.map(() => colors.alpha.green),
					borderColor: this.chartData.datasets[0].borderColor.map(() => colors.plain.green),
					borderWidth: 1,
					maxBarThickness: 150
				};

				data.forEach(d => {
					const c_idx = this.chartData.campaignId.indexOf(d.campaign_id);
					let click_rate = (100 * (d.clicked / d.sent)).toFixed(1);
					let normalized = ((click_rate/d.avg_difficulty)*minAvgDiff).toFixed(1);
					dataset.data[c_idx] = this.currentChart == this.NORMALIZED? normalized : click_rate;
					dataset.sent[c_idx] = d.sent;
					dataset.clicked[c_idx] = d.clicked;
					let planned_start = this.$moment.utc(d.planned_start).local().locale(this.$i18n.locale).format("YYYY-MM-DD");
					let planned_end = this.$moment.utc(d.planned_end).local().locale(this.$i18n.locale).format("YYYY-MM-DD");
					dataset.period[c_idx] = planned_start + " - " + planned_end;
				});

				this.chartData.datasets[1] = dataset;
				this.drawAverageDifficultyYAxis(false);
			}
		},
		removeDataset() {
			if(this.currentChart == this.CAMP_AVG) {
				this.chartData.datasets[1] = this.drawAverageDifficulty(this.chartData.datasets[0].avgDifficulty);
				this.drawAverageDifficultyYAxis(true);
			} else {
				if(this.chartData.datasets[1]) {
					this.chartData.datasets.pop();
				}
				
				this.drawAverageDifficultyYAxis(false);
			}	
		},
		// Register the click event on the chart
		addClickHandler() {
			var canvas = this.$refs.chart.$data._chart.canvas;
			canvas.onclick = this.handleClick;
			canvas.onmousemove = this.handleHover;
		},
		createLegend() {
			this.$refs.chart.addPlugin({
				id: 'custom-legend',
				beforeDraw: (chart) => {
					var legendItems = chart.legend.legendItems;
					legendItems.forEach((item, index) => {
						item.lineWidth = 1;
						item.fillStyle = this.currentChart == this.NORMALIZED?
							pattern.draw('diagonal', colors.palette.dark[index % colors.palette.dark.length]) : colors.palette.dark[index % colors.palette.dark.length];
					});
				}
			});
		},
		// Set the options for the chart
		setOptions() {
			this.options = {
				scales: {
					xAxes: [
						{
							ticks: {
								callback: function (value) {
									return value ? truncateStr(value, 20) : "N.A.";
								}
							},
							gridLines: {
								display: false
							}
						}
					],
					yAxes: [
						{
							stacked: false,
							position: "left",
							id: "y",
							ticks: {
								min: 0,
								callback: function (value) {
									return value + "%";
								}
							},
							scaleLabel: {
								display: true
							}
						}, {
							stacked: false,
							display: false,
							position: "right",
							id: "y-axis-avg",
							scaleLabel: {
								display: false
							},
							gridLines: {
								display: false
							},
							ticks: {
								min: 0
							}
						}
					]
				},
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					animateRotate: true,
					animateScale: true
				},
				legend: {
					display: true,
					labels: {
						fontColor: colors.plain.primary,
						fontSize: 16,
						padding: 20,
					},
					onClick: this.handleLegendClick
				},
				tooltips: {
					backgroundColor: colors.plain.white,
					titleFontColor: colors.plain.primary,
					bodyFontColor: colors.plain.primary,
					borderColor: colors.plain.grey,
					borderWidth: 0.5,
					cornerRadius: 10,
					xPadding: 15,
					yPadding: 10,
					position: 'custom',
					callbacks: {
						label: (tooltipItem) => {
							return this.$t('Dashboard.Charts.SuccessfulAttacks') + " " + tooltipItem.value + "%";
						},
						afterBody: (tooltipItem, data) => {
							let body = [];

							let datasetIndex = tooltipItem[0].datasetIndex;
							let index = tooltipItem[0].index;

							if(data.datasets[datasetIndex].type === 'line') {
								return;
							}

							let sent = data.datasets[datasetIndex].sent[index];
							let clicked = data.datasets[datasetIndex].clicked[index];
							let period = data.datasets[datasetIndex].period[index];

							body.push("");
							body.push(this.$t('Dashboard.Charts.AttacksSent') + ": " + sent);
							body.push(this.$t('Dashboard.Charts.TotalClicks') + ": " + clicked);
							body.push(this.$t('Dashboard.Charts.CampaignPeriod') + ": " + period);

							if(datasetIndex == 0) {
								let avg_diff = data.datasets[datasetIndex].avgDifficulty[index];
								body.push(this.$t('Analytics.AverageDifficulty') + ": " + avg_diff);
							}
							
							return body;
						}
					}
				},
				plugins: {
					datalabels: {
						formatter: function (_value, context) {
							// Hide labels for line chart type
							const isLineType = context.dataset.type == 'line';
							
							return isLineType? '' : (_value > 0 ? parseFloat(_value).toFixed(1) + "%" : "");
						},
						clamp: true,
						clip: true
					}
				}
			};
		},
		// Highligth the selected bar in the chart
		highlightDataset(elementIndex, active) {
			if (active) {
                this.chartData.datasets[0].backgroundColor[elementIndex] = this.currentChart == this.NORMALIZED? pattern.draw('diagonal', colors.alpha.blue) : colors.alpha.blue;
                this.chartData.datasets[0].borderColor[elementIndex] = colors.plain.blue;
            } else {
                this.chartData.datasets[0].backgroundColor[elementIndex] = this.currentChart == this.NORMALIZED? pattern.draw('diagonal', colors.alpha.lightBlue) : colors.alpha.lightBlue;
                this.chartData.datasets[0].borderColor[elementIndex] = colors.alpha.lightBlue;
            }

			if (this.currentChart == this.NORMALIZED && this.chartData.datasets[1]) {
				this.chartData.datasets[1].backgroundColor = 
					this.chartData.datasets[0].backgroundColor.map(() => pattern.draw('diagonal', colors.alpha.green));
			}
		},
		// Handle the click event
		handleClick(evt) {
			const chart = this.$refs.chart.$data._chart;
			let activeElement = chart.getElementAtEvent(evt);
			var xScale = chart.scales['x-axis-0'];
			let elemLabel = xScale.getValueForPixel(evt.x);

			// Get chart boundaries
			let resetCoordinates = this.$refs.chart.$data._chart.canvas.getBoundingClientRect();

			// Get label offset, because getValueForPixel is not so precise
			var xLabelOffset = (xScale.getPixelForTick(elemLabel) - xScale.getPixelForTick(elemLabel-1)) / 2;

			// Check if click happened on valid label
			var isClickOnLabel = resetCoordinates.bottom - evt.y < 20;

			// Find Campaign index to filter for
			let index = null;
			if (activeElement && activeElement.length > 0 && 0 === activeElement[0]._datasetIndex) {
				index = activeElement[0]._index;
			} else if(isClickOnLabel) {
				index = xScale.getValueForPixel(evt.x-xLabelOffset);
			}

			if(index > -1) {
				this.myselfClicked = true;
				const reset = this.selectedIndex == index;
				this.selectedIndex = reset ? null : index;
				this.ads.setFilter(this.myself, reset ? null : this.chartData.campaignId[index], this.chartData.labels[index]);
			}
		},
		handleHover(evt) {
			if (this.chartData) {
				var chart = this.$refs.chart.$data._chart;
				var activeElement = chart.getElementAtEvent(evt);
				if (activeElement.length > 0) {
					activeElement = activeElement[0];
					if (activeElement._datasetIndex == 0) {
						evt.target.style.cursor = 'pointer';
						this.highlightDataset(activeElement._index, true);
					}
					this.$refs.chart.$data._chart.update();
				} else {
					evt.target.style.cursor = 'default';
					this.toggleActive();
				}
			}
		},
		// Handle the click event on the legend
		handleLegendClick(e, legendItem) {
			let index = legendItem.datasetIndex;
			let chart = this.$refs.chart.$data._chart;
			let alreadyHidden = !chart.getDatasetMeta(index).hidden ? false : chart.getDatasetMeta(index).hidden;

			chart.data.datasets.forEach((dataset, datasetindex) => {
				let meta = chart.getDatasetMeta(datasetindex);

				if (chart.data.datasets.length === 1) {
					meta.hidden = !meta.hidden;
				} else {
					meta.hidden = !alreadyHidden && meta.hidden ? !meta.hidden && !alreadyHidden : datasetindex !== index;
				}
			});

			this.$refs.chart.$data._chart.update();
		},
		// Toggle active status
		toggleActive() {
			const elementIndex = this.selectedIndex;
			const myselfActive = this.ads.filter[this.myself];
			const othersActive = this.ads.hasOtherValidFilters(this.myself); 
			this.myselfClicked = false;

			this.chartData.datasets[0].data.forEach((value, index) => {
				this.chartData.datasets[0].active[index] = (index == elementIndex) && myselfActive;
				this.highlightDataset(index, myselfActive ? this.chartData.datasets[0].active[index] : !othersActive);
			});
			
			this.$refs.chart.$data._chart.update();
		},
		removeFilter(event) {
			if (event.target.classList.contains("close")) {
				this.ads.setFilter(event.target.getAttribute("filter"), null);
			}
		}
	}
}
</script>

<style lang="less">
#clickRateByCampaign {
	min-height: 491px;
	.card-title {
		.expand-icon,
		.compress-icon {
			position: absolute;
			right: 15px;
			width: 1rem;
			cursor: pointer;
		}
		.bc-dropdown {
			cursor: pointer;
			font-size: 1rem;
			.dropdown-toggle::after {
				display: none;
			}
			.btn-secondary {
				color: @cg-primary-dark-text-color;
				background-color: transparent;
				border: none;
			}
		}
	}
	.tab-dropdown {
		position: absolute;
		right: 15px;
		cursor: pointer;
		font-size: 1rem;
		.dropdown-menu {
			width: 350px;
			.dropdown-item {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}
		.dropdown-toggle::after {
			display: none;
		}
		.btn-secondary {
			color: @cg-primary-dark-text-color;
			background-color: transparent;
			border: none;
			margin-right: 20px;
		}
	}
}

// Large devices (desktops, 992px and up)
@media only screen and (max-width: 991px) {
	#clickRateByCampaign {
		.card-title {
			.expand-icon,
			.compress-icon {
				display: none;
			}
		}
	}
}
</style>